import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { Search, Loader2, ArrowLeft, Download } from 'lucide-react';
import * as Sentry from '@sentry/react';

import { streamSearch } from '@/services/external-chat/stream';
import { getExternalChatFacet } from '@/services/external-chat/get-facet';
import type {
  SearchResult,
  SearchResultsEvent,
  SearchFilterConfig,
} from '@/services/external-chat/types';
import { SearchResultCard } from './components/search-result-card';
import { SearchSummary } from './components/search-summary';
import { FilterBar } from './components/filter-bar';
import { EmbedChatPage } from './embed-chat-page';
import { useEmbedStyles } from '@/features/external-chat/hooks/use-embed-styles';

// Filter types that need live facet values. Others (date_range, text) don't
// need a value list from Qdrant.
const KEYWORD_FILTER_TYPES = new Set(['select', 'multiselect', 'keyword']);
const isKeywordFilter = (type: string) => KEYWORD_FILTER_TYPES.has(type);

export const EmbedSearchPage = () => {
  const { orgId, collectionId } = useParams<{
    orgId: string;
    collectionId: string;
  }>();
  const [searchParams] = useSearchParams();

  const apiKey = searchParams.get('apiKey') || '';
  const primaryColor = searchParams.get('primaryColor') || '#2563eb';
  const fontColor = searchParams.get('fontColor') || '#111827';
  const bgColor = searchParams.get('bgColor') || '#ffffff';
  const linkColor = searchParams.get('linkColor') || primaryColor;
  const title = searchParams.get('title') || 'Search';

  const headerFont = searchParams.get('headerFont') || '';
  const headerFontWeight = searchParams.get('headerFontWeight') || '';
  const bodyFont = searchParams.get('bodyFont') || '';
  const bodyFontWeight = searchParams.get('bodyFontWeight') || '';
  const searchBgColor = searchParams.get('searchBgColor') || '';
  const searchTextColor = searchParams.get('searchTextColor') || '';
  const customCss = searchParams.get('customCss') || '';
  const borderRadius = searchParams.get('borderRadius') || '8px';
  const contentMaxWidth = searchParams.get('contentMaxWidth') || '';

  const filterConfig: SearchFilterConfig[] = useMemo(() => {
    try {
      const raw = searchParams.get('filters');
      return raw ? JSON.parse(raw) : [];
    } catch {
      return [];
    }
  }, [searchParams]);

  // Live facet options per field, fetched on mount using the embed API key.
  // Stale URL-baked values would drift the moment content is ingested/removed,
  // so we always hydrate from Qdrant.
  const [facetOptionsByField, setFacetOptionsByField] = useState<
    Record<string, string[]>
  >({});
  const [facetLoadingByField, setFacetLoadingByField] = useState<
    Record<string, boolean>
  >({});
  const [facetErrorByField, setFacetErrorByField] = useState<
    Record<string, string>
  >({});

  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [totalResults, setTotalResults] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [summary, setSummary] = useState('');
  const [summaryStatus, setSummaryStatus] = useState<string | undefined>();
  const [isSummaryStreaming, setIsSummaryStreaming] = useState(false);
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  const [hasSearched, setHasSearched] = useState(false);
  const [searchError, setSearchError] = useState<string | undefined>();

  const [showChat, setShowChat] = useState(false);
  const [chatSeedQuery, setChatSeedQuery] = useState('');
  const [chatSeedSummary, setChatSeedSummary] = useState('');
  const [chatSearchContext, setChatSearchContext] = useState('');

  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEmbedStyles({
    headerFont,
    headerFontWeight,
    bodyFont,
    bodyFontWeight,
    customCss,
    scope: 'search',
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Hydrate live facet values for each keyword filter on mount. Requests run
  // in parallel with per-field loading/error state so the FilterBar can show
  // progressive UI instead of a blank dropdown.
  useEffect(() => {
    if (!orgId || !collectionId || !apiKey) return;
    const keywordFilters = filterConfig.filter((f) => isKeywordFilter(f.type));
    if (keywordFilters.length === 0) return;

    const controller = new AbortController();

    const initialLoading: Record<string, boolean> = {};
    for (const f of keywordFilters) initialLoading[f.field] = true;
    setFacetLoadingByField(initialLoading);
    setFacetErrorByField({});

    Promise.all(
      keywordFilters.map(async (f) => {
        try {
          const res = await getExternalChatFacet({
            orgId,
            collectionId,
            apiKey,
            field: f.field,
            signal: controller.signal,
          });
          const values = res.hits
            .map((h) => h.value)
            .filter((v) => v != null && v !== '');
          setFacetOptionsByField((prev) => ({ ...prev, [f.field]: values }));
        } catch (err) {
          if (controller.signal.aborted) return;
          const msg = err instanceof Error ? err.message : String(err);
          setFacetErrorByField((prev) => ({ ...prev, [f.field]: msg }));
        } finally {
          if (!controller.signal.aborted) {
            setFacetLoadingByField((prev) => ({ ...prev, [f.field]: false }));
          }
        }
      }),
    );

    return () => controller.abort();
  }, [orgId, collectionId, apiKey, filterConfig]);

  // Merge live facet values into filterConfig so existing FilterBar code
  // continues to read `f.options` without caring about fetch plumbing.
  const hydratedFilterConfig = useMemo<SearchFilterConfig[]>(() => {
    return filterConfig.map((f) => {
      if (!isKeywordFilter(f.type)) return f;
      return { ...f, options: facetOptionsByField[f.field] || [] };
    });
  }, [filterConfig, facetOptionsByField]);

  const runSearch = useCallback(
    async (page: number, skipSummary: boolean) => {
      const trimmed = query.trim();
      if (!trimmed || isSearching || !orgId || !collectionId || !apiKey) return;

      setIsSearching(true);
      setHasSearched(true);
      setResults([]);
      setSearchError(undefined);
      // Only reset/stream summary on a fresh search. Pagination keeps the
      // existing summary on screen — BE respects skip_summary and returns
      // just the new page of results.
      if (!skipSummary) {
        setSummary('');
        setSummaryStatus(undefined);
        setIsSummaryStreaming(true);
      }
      setCurrentPage(page);

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const activeFilters: Record<string, string> = {};
      for (const [k, v] of Object.entries(filterValues)) {
        if (v) activeFilters[k] = v;
      }

      await streamSearch({
        orgId,
        collectionId,
        apiKey,
        query: trimmed,
        page,
        filters:
          Object.keys(activeFilters).length > 0 ? activeFilters : undefined,
        skipSummary,
        signal: controller.signal,
        onResults: (data: SearchResultsEvent) => {
          setResults(data.items);
          setTotalResults(data.total);
          setTotalPages(data.pages);
          setCurrentPage(data.page);
          setIsSearching(false);
        },
        onToken: (token) => {
          if (skipSummary) return;
          setSummary((prev) => prev + token);
          setSummaryStatus(undefined);
        },
        onStatus: (s) => {
          if (skipSummary) return;
          setSummaryStatus(s);
        },
        onError: (error) => {
          Sentry.captureException(new Error(error), {
            extra: { context: 'embed-search', orgId, collectionId },
          });
          setSearchError(error || 'Search failed. Please try again.');
          setIsSearching(false);
          if (!skipSummary) setIsSummaryStreaming(false);
        },
        onDone: () => {
          if (!skipSummary) setIsSummaryStreaming(false);
        },
      });
    },
    [query, isSearching, orgId, collectionId, apiKey, filterValues],
  );

  const handleSearch = useCallback(
    (page = 1) => runSearch(page, false),
    [runSearch],
  );

  const handlePageChange = useCallback(
    (page: number) => runSearch(page, true),
    [runSearch],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSearch(1);
      }
    },
    [handleSearch],
  );

  const handleDiveDeeper = useCallback(() => {
    const context = results
      .map((r) => `## ${r.title}\nURL: ${r.url}\n${r.snippet}`)
      .join('\n\n');
    setChatSeedQuery(query);
    setChatSeedSummary(summary);
    setChatSearchContext(context);
    setShowChat(true);
  }, [query, summary, results]);

  const handleExport = useCallback(() => {
    if (results.length === 0) return;
    const lines = results.map((r) => `${r.title}\n${r.url}\n${r.snippet}\n`);
    const blob = new Blob([lines.join('\n---\n\n')], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `search-results-${query.slice(0, 30).replace(/\s+/g, '-')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }, [results, query]);

  const handleFilterChange = useCallback((field: string, value: string) => {
    setFilterValues((prev) => ({ ...prev, [field]: value }));
  }, []);

  if (!orgId || !collectionId) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white p-4 text-center text-sm text-gray-500'>
        Missing required parameters (orgId, collectionId).
      </div>
    );
  }

  if (!apiKey) {
    return (
      <div className='flex h-dvh items-center justify-center bg-white p-4 text-center text-sm text-gray-500'>
        Missing API key. Add <code className='text-xs'>?apiKey=eak_...</code> to
        the URL.
      </div>
    );
  }

  if (showChat) {
    return (
      <div
        className='flex h-dvh flex-col'
        style={{
          backgroundColor: bgColor,
          color: fontColor,
          fontFamily: bodyFont || undefined,
          fontWeight: bodyFontWeight || undefined,
        }}
      >
        <header
          className='flex shrink-0 items-center gap-2 px-4 py-3 text-white shadow-sm'
          style={{ backgroundColor: primaryColor }}
        >
          <button
            onClick={() => setShowChat(false)}
            className='p-1 hover:opacity-80'
          >
            <ArrowLeft className='size-5' />
          </button>
          <h1
            className='text-sm font-semibold'
            style={{
              fontFamily: headerFont || undefined,
              fontWeight: headerFontWeight || undefined,
            }}
          >
            Dive Deeper
          </h1>
        </header>
        <div className='flex-1 overflow-hidden'>
          <EmbedChatPage
            seedQuery={chatSeedQuery}
            seedSummary={chatSeedSummary}
            searchContext={chatSearchContext}
            embedded
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className='flex h-dvh flex-col'
      style={{
        backgroundColor: bgColor,
        color: fontColor,
        fontFamily: bodyFont || undefined,
        fontWeight: bodyFontWeight || undefined,
      }}
    >
      <header
        className='flex shrink-0 items-center gap-2 px-4 py-3 text-white shadow-sm'
        style={{ backgroundColor: primaryColor }}
      >
        <Search className='size-5' />
        <h1
          className='text-sm font-semibold'
          style={{
            fontFamily: headerFont || undefined,
            fontWeight: headerFontWeight || undefined,
          }}
        >
          {title}
        </h1>
      </header>

      <div
        className='shrink-0 border-b px-4 py-3'
        style={{ borderColor: fontColor + '15' }}
      >
        <div
          className={`mx-auto ${contentMaxWidth ? '' : 'max-w-2xl'}`}
          style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
        >
          <div className='flex items-center gap-2'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 size-4 -translate-y-1/2 opacity-30' />
              <input
                ref={inputRef}
                type='text'
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder='Search...'
                className='w-full border py-2.5 pl-10 pr-4 text-sm outline-none'
                style={{
                  color: searchTextColor || fontColor,
                  borderColor: fontColor + '25',
                  backgroundColor: searchBgColor || fontColor + '05',
                  borderRadius,
                }}
              />
            </div>
            <button
              onClick={() => handleSearch(1)}
              disabled={!query.trim() || isSearching}
              className='flex size-10 shrink-0 items-center justify-center text-white transition-opacity disabled:opacity-40'
              style={{ backgroundColor: primaryColor, borderRadius }}
            >
              {isSearching ? (
                <Loader2 className='size-4 animate-spin' />
              ) : (
                <Search className='size-4' />
              )}
            </button>
          </div>
          {filterConfig.length > 0 && (
            <div className='mt-2'>
              <FilterBar
                filters={hydratedFilterConfig}
                values={filterValues}
                onChange={handleFilterChange}
                fontColor={fontColor}
                borderRadius={borderRadius}
                loadingByField={facetLoadingByField}
                errorByField={facetErrorByField}
              />
            </div>
          )}
        </div>
      </div>

      <div className='flex-1 overflow-y-auto px-4 py-4'>
        <div
          className={`mx-auto space-y-4 ${contentMaxWidth ? '' : 'max-w-2xl'}`}
          style={contentMaxWidth ? { maxWidth: contentMaxWidth } : undefined}
        >
          {!hasSearched && (
            <div className='flex h-full items-center justify-center pt-20'>
              <p className='text-sm opacity-40'>Enter a query to search.</p>
            </div>
          )}

          {hasSearched && (
            <>
              {searchError && (
                <div
                  className='border px-3 py-2 text-sm'
                  style={{
                    borderColor: '#dc262640',
                    backgroundColor: '#dc26260a',
                    color: '#b91c1c',
                    borderRadius,
                  }}
                  role='alert'
                >
                  {searchError}
                </div>
              )}

              {!searchError &&
                !isSearching &&
                !isSummaryStreaming &&
                results.length === 0 &&
                !summary && (
                  <div className='flex items-center justify-center pt-10'>
                    <p className='text-sm opacity-50'>
                      No results found for &ldquo;{query}&rdquo;.
                    </p>
                  </div>
                )}

              {(summary || summaryStatus || isSummaryStreaming) && (
                <SearchSummary
                  summary={summary}
                  isStreaming={isSummaryStreaming}
                  status={summaryStatus}
                  linkColor={linkColor}
                  fontColor={fontColor}
                  primaryColor={primaryColor}
                  onDiveDeeper={handleDiveDeeper}
                  borderRadius={borderRadius}
                />
              )}

              {results.length > 0 && (
                <div className='flex items-center justify-between'>
                  <span className='text-xs opacity-50'>
                    {totalResults} results (page {currentPage} of {totalPages})
                  </span>
                  <button
                    onClick={handleExport}
                    className='flex items-center gap-1 rounded-md px-2 py-1 text-xs opacity-50 transition-opacity hover:opacity-80'
                  >
                    <Download className='size-3' />
                    Export
                  </button>
                </div>
              )}

              {isSearching && results.length === 0 && !isSummaryStreaming && (
                <div
                  className='flex items-center justify-center gap-2 py-6 text-xs opacity-60'
                  role='status'
                  aria-live='polite'
                >
                  <Loader2 className='size-4 animate-spin' />
                  <span>Loading results...</span>
                </div>
              )}

              <div className='space-y-2'>
                {results.map((r, i) => (
                  <SearchResultCard
                    key={`${r.title}-${i}`}
                    result={r}
                    query={query}
                    fontColor={fontColor}
                    linkColor={linkColor}
                    borderRadius={borderRadius}
                  />
                ))}
              </div>

              {totalPages > 1 && (
                <div className='flex items-center justify-center gap-2 pt-2'>
                  <button
                    disabled={currentPage <= 1 || isSearching}
                    onClick={() => handlePageChange(currentPage - 1)}
                    className='border px-3 py-1.5 text-xs disabled:opacity-30'
                    style={{ borderColor: fontColor + '25', borderRadius }}
                  >
                    Previous
                  </button>
                  <span className='flex items-center gap-1.5 text-xs opacity-50'>
                    {isSearching && <Loader2 className='size-3 animate-spin' />}
                    {currentPage} / {totalPages}
                  </span>
                  <button
                    disabled={currentPage >= totalPages || isSearching}
                    onClick={() => handlePageChange(currentPage + 1)}
                    className='border px-3 py-1.5 text-xs disabled:opacity-30'
                    style={{ borderColor: fontColor + '25', borderRadius }}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
